import { Icon20CalendarOutline, Icon20InfoCircleOutline } from '@vkontakte/icons';
import { Button as ButtonVKUI, Input, Spacing, Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import { parse, stringify } from 'qs';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { IS_WEBVIEW, IS_WEBVIEW_FROM_UA } from 'reactApp/appHelpers/configHelpers';
import { activatePromocodeRequest } from 'reactApp/modules/quotaLanding/quotaLanding.module';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getIdByStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { usePromocodeDetails } from 'reactApp/sections/QuotaLanding/PromocodeDialog/hooks/usePromocodeDetails';
import type { PromocodeDialogProps } from 'reactApp/sections/QuotaLanding/PromocodeDialog/PromocodeDialog.types';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { renderUserChangeModal } from 'reactApp/ui/Mobile/UserChangeModal/UserChangeModal.helpers';
import { type EPromocodeApiErrorCodes, errorShort } from 'reactApp/ui/Promocode/promocode.constants';
import { sendDwh } from 'reactApp/utils/ga';
import opener from 'reactApp/utils/opener';

import styles from './PromocodeDialogMobile.css';

const sendRadar = (action: string, label?: string) => {
    sendDwh({
        eventCategory: 'modal-promocodes',
        action,
        label,
        dwhData: {
            platform: 'touch',
        },
    });
};

const isWebview = IS_WEBVIEW || IS_WEBVIEW_FROM_UA;

export const PromocodeDialogMobile = memo<PromocodeDialogProps>(({ onClose }) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    const dispatch = useDispatch();
    const { search, pathname } = useLocation();
    const { titleMobile, promocode, conditions } = usePromocodeDetails();
    const email = useSelector(UserSelectors.getEmail);

    const [isActivated, setActivated] = useState(false);
    const [promocodeValue, setPromocode] = useState(promocode);
    const [error, setError] = useState<EPromocodeApiErrorCodes | null>(null);
    const [isInProgress, setInProgress] = useState(false);

    useEffect(() => {
        sendRadar('show');
    }, []);

    const onActivate = useCallback(() => {
        setActivated(true);
        setInProgress(false);
    }, [setActivated, setInProgress]);

    const onError = useCallback(
        (code: EPromocodeApiErrorCodes) => {
            setInProgress(false);
            setError(code);
        },
        [setInProgress, setError]
    );

    const handleClose = useCallback(() => {
        sendRadar('close');
        onClose?.();
    }, [onClose]);

    const handleActivate = useCallback(() => {
        if (promocodeValue) {
            sendRadar('click', 'activate');
            setInProgress(true);
            dispatch(
                activatePromocodeRequest({
                    promocode: promocodeValue,
                    onActivate,
                    onError,
                })
            );
        }
    }, [promocodeValue, dispatch, setInProgress, onActivate, onError]);

    const handleChangePromocode = useCallback(
        (event) => {
            const { value } = event.target || {};
            setPromocode(value);
            if (error) {
                setError(null);
            }
        },
        [setPromocode, setError, error]
    );

    const handleChangeAccount = useCallback(() => {
        if (isWebview) {
            return;
        }
        onClose?.();
        const query = parse(search, { ignoreQueryPrefix: true }) || {};
        query.action = 'request-promocode';
        dispatch(
            historyPush({
                id: pathname,
                search: stringify(query, { addQueryPrefix: true }),
            })
        );
        renderUserChangeModal({ reloginAction: false, isRebrandingQuotaLanding: true });
    }, [onClose, dispatch, search, pathname]);

    const handleGoToSubscriptions = useCallback(() => {
        onClose?.();
        opener(getIdByStorage(EStorageType.subscriptions), !!(IS_WEBVIEW || IS_WEBVIEW_FROM_UA));
    }, [dispatch, onClose]);

    return (
        <MobileDialog
            id="promocode-modal-mobile"
            onClose={handleClose}
            topmost
            closeOnDimmerClick
            scrollRef={scrollRef}
            mod="base"
            contextPadding="zero"
            isRebrandingQuotaLanding
            closeMode="darkAndRound"
        >
            <div ref={scrollRef} className={styles.rootMobile}>
                {titleMobile}
                {Boolean(email) && !isActivated && (
                    <>
                        <Spacing size={20} />
                        <Text className={styles.description}>
                            Промокод будет активирован для&nbsp;аккаунта{' '}
                            <ButtonVKUI
                                mode="link"
                                title="Сменить аккаунт"
                                onClick={handleChangeAccount}
                                className={classNames(styles.buttonLink, {
                                    [styles.disabled]: isWebview,
                                })}
                            >
                                {email}
                            </ButtonVKUI>
                        </Text>
                        <Spacing size={48} />
                    </>
                )}
                {isActivated ? (
                    <>
                        <Spacing size={46} />
                        <Text className={styles.activated}>
                            Промокод добавлен на страницу «Мои&nbsp;подписки». Перейдите, чтобы завершить активацию
                        </Text>
                        <Spacing size={16} />
                        <Button theme="vk" secondary sizeMode={ButtonSizeMode.extraBig} onClick={handleGoToSubscriptions}>
                            Мои подписки
                        </Button>
                    </>
                ) : (
                    <>
                        <Input
                            type="text"
                            placeholder="Введите промокод"
                            value={promocodeValue}
                            className={classNames(styles.input, {
                                [styles.inputError]: Boolean(error),
                            })}
                            onChange={handleChangePromocode}
                        />
                        {error && (
                            <>
                                <Spacing size={12} />
                                <Text className={styles.error}>{errorShort[error]}</Text>
                            </>
                        )}
                        <Spacing size={12} />
                        <Button
                            theme="vk"
                            secondary
                            sizeMode={ButtonSizeMode.extraBig}
                            onClick={handleActivate}
                            disabled={!promocodeValue || isInProgress}
                        >
                            Активировать
                        </Button>
                    </>
                )}
                {conditions && (
                    <>
                        <Spacing size={60} />
                        <div className={styles.conditions}>
                            {conditions.map((item, index) => {
                                const icon = index ? <Icon20InfoCircleOutline /> : <Icon20CalendarOutline />;
                                return (
                                    <div className={styles.condition} key={`condition-${index}`}>
                                        {icon}
                                        <Text className={styles.conditionText}>{item}</Text>
                                    </div>
                                );
                            })}
                        </div>
                        <Spacing size={16} />
                        <ButtonVKUI
                            mode="link"
                            href="https://promoimages.hb.ru-msk.vkcs.cloud/promocodes_terms.pdf"
                            target="_blank"
                            className={styles.fullConditions}
                        >
                            Полные условия
                        </ButtonVKUI>
                        <Spacing size={32} />
                    </>
                )}
            </div>
        </MobileDialog>
    );
});

PromocodeDialogMobile.displayName = 'PromocodeDialogMobile';
